import { SerializedPage } from '@lidojs/design-core';
import { Hyperlink, Popover, useEditor } from '@lidojs/design-editor';
import { Button, Checkbox, Input, Select, SelectItem, Spinner, addToast } from "@heroui/react";
import { useEffect, useRef, useState } from 'react';
import { downloadBlob, downloadObjectAsHTML, downloadObjectAsJson, uploadPageHTMLAndGetURL } from '../utils/download';
import { getBrands, getCompositeVideo, getPreviewVideo } from '../services/creative/api';
import { generatePageLabels } from 'libs/design-editor/src/ultils/generatePageLabels';

export const ExportButton = () => {
  const exportButtonRef = useRef<HTMLButtonElement>(null);
  const [popoverBox, setPopoverBox] = useState<HTMLDivElement | null>(null);
  const [openExportSetting, setOpenExportSetting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [typeExport, setTypeExport] = useState(new Set(['html']));
  const [pageExport, setPageExport] = useState(new Set(['all']));
  const [videoDuration, setVideoDuration] = useState(6);
  const [isComposite, setIsComposite] = useState(false);
  const [brand, setBrand] = useState('');
  const [dateTime, setDateTime] = useState('');
  const [brands, setBrands] = useState<string[]>([]);
  const { query,pagesState } = useEditor((state) => ({
    pagesState: state.pages,
  }));


  const pages = query.serialize();

  const itemsExport = [
    { label: 'HTML', value: 'html' },
    { label: 'JSON', value: 'json' },
    { label: 'Video', value: 'video' },
  ];

  const isVideoExport = typeExport.has('video');

  const itemPages = [
    { label: 'All Pages', id: 'all' },
    ...generatePageLabels(pagesState).filter((page) => page.label.startsWith("Page"))
  ];

  // A video is always generated from a single page, so "All Pages" isn't a valid choice here.
  const itemPagesForExport = isVideoExport
    ? itemPages.filter((page) => page.id !== 'all')
    : itemPages;

  const onChangeTypeExport = (value) => {
    setTypeExport(new Set(value));
  };

  const onChangePageExport = (value) => {
    setPageExport(new Set(value));
  };

  useEffect(() => {
    if (isVideoExport && pageExport.has('all') && itemPagesForExport[0]) {
      setPageExport(new Set([itemPagesForExport[0].id]));
    }
  }, [typeExport]);

  useEffect(() => {
    if (isComposite && brands.length === 0) {
      getBrands()
        .then(setBrands)
        .catch((error) => {
          addToast({
            title: 'Could not load brands',
            description: error?.message || 'Failed to fetch the available brands.',
            color: 'danger',
          });
        });
    }
  }, [isComposite]);

  const hasValidRootUrl = (page: SerializedPage) => {
    const hyperlink = page.layers.ROOT.props?.hyperlink as Hyperlink | undefined;
    return Boolean(hyperlink?.hasHyperlink && hyperlink?.linkRedirect?.trim());
  };

  const onDownloadVideo = async () => {
    const selectedPageIndex = Array.from(pageExport)[0];
    const page = pages[selectedPageIndex];

    // A video is just a visual recording, not an interactive ad, so it doesn't need
    // the clickthrough URL that HTML/JSON export requires.
    if (!page) {
      addToast({
        title: 'Download failed',
        description: 'Select a page to export.',
        color: 'danger',
      });
      return;
    }

    if (isComposite && !brand) {
      addToast({
        title: 'Download failed',
        description: 'Select a brand for the composite video.',
        color: 'danger',
      });
      return;
    }

    if (isComposite && !dateTime.trim()) {
      addToast({
        title: 'Download failed',
        description: 'Set the date/time to overlay on the composite video.',
        color: 'danger',
      });
      return;
    }

    setIsLoading(true);

    try {
      const url = await uploadPageHTMLAndGetURL({ page, pages });

      // <input type="datetime-local"> gives "YYYY-MM-DDTHH:mm" — a plain local wall-clock
      // string with no timezone attached. Just swap the separator; never parse it into a
      // Date, so the value drawn on the video is exactly what was picked, no UTC shift.
      const compositeDateTime = dateTime.replace('T', ' ');

      const blob = isComposite
        ? await getCompositeVideo({ url, brand, dateTime: compositeDateTime, creativeDuration: videoDuration })
        : await getPreviewVideo({ url, duration: videoDuration });

      downloadBlob(blob, isComposite ? `composite-${brand}.mp4` : 'preview.mp4');
    } catch (error) {
      addToast({
        title: 'Download failed',
        description: error?.message || 'An error occurred while generating the video.',
        color: 'danger',
      });
      console.error('Video download failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const onDownload = async () => {
    if (isVideoExport) {
      return onDownloadVideo();
    }

    const processAllPages = pageExport.has('all');
    const selectedPageIndex = Array.from(pageExport)[0];

    const pagesToDownload = typeExport.has('html')
      ? (processAllPages ? pages.filter(item => !item.layers.ROOT.props?.relatesTo) : [pages[selectedPageIndex]])
      : (processAllPages ? pages : [pages[selectedPageIndex]]);

    if (pagesToDownload.some(page => !hasValidRootUrl(page))) {
      addToast({
        title: 'Download failed',
        description: 'The creative root layer must have a valid URL before it can be exported.',
        color: 'danger'
      });
      return;
    }

    setIsLoading(true);

    try {
      const fileNamePrefix = 'file';

      const downloadHTML = async (pagesToDownload: SerializedPage[]) => {
        await Promise.all(pagesToDownload.map(page => downloadObjectAsHTML(page, pages)));
      };

      const downloadJSON = (pages: SerializedPage[], fileName: string) => {
        downloadObjectAsJson(fileName, pages);
      };

      if (typeExport.has('html')) {
        await downloadHTML(pagesToDownload);
      } else {
        const fileName = processAllPages
          ? fileNamePrefix
          : `${fileNamePrefix} ${Number(selectedPageIndex) + 1}`;

        downloadJSON(pagesToDownload, fileName);
      }
    } catch (error) {
      addToast({
        title: 'Download failed',
        description: error?.message || 'An error occurred during the download process.',
        color: 'danger'
      })
      console.error('Download failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const selectedPageIndex = Array.from(pageExport)[0];
    if (selectedPageIndex !== 'all' && !pages[selectedPageIndex]) {
      setPageExport(new Set(['all']));
    }
  }, [pages, pageExport]);

  return (
    <>
      <Button
        ref={exportButtonRef}
        css={{
          fontWeight: 700,
          padding: 0,
          ':hover': {
            textDecoration: 'underline',
            backgroundColor: 'inherit',
          },
        }}
        size="lg"
        variant="light"
        onClick={() => setOpenExportSetting(true)}
      >
        Export
      </Button>
      <Popover
        ref={setPopoverBox}
        anchorEl={exportButtonRef.current}
        offsets={{
          'bottom-end': { x: 0, y: 8 },
        }}
        open={openExportSetting}
        placement={'bottom-end'}
        onClose={() => setOpenExportSetting(false)}
      >
        <div css={{ padding: 16, minWidth: 220, display: 'grid', rowGap: 8 }}>
          <div>
            <label>File type</label>
            <Select
              aria-label='fileType'
              className="max-w-xs text-black"
              color="primary"
              items={itemsExport}
              name="fileType"
              popoverProps={{ portalContainer: popoverBox ?? undefined }}
              selectedKeys={typeExport}
              variant="bordered"
              onSelectionChange={onChangeTypeExport}
            >
              {(item) => <SelectItem key={item.value}>{item.label}</SelectItem>}
            </Select>
          </div>
          {pages?.length > 1 && (
            <div>
              <label>{isVideoExport ? 'Select page' : 'Select pages'}</label>
              <Select
                aria-label='selectPages'
                className="max-w-xs text-black"
                color="primary"
                items={itemPagesForExport}
                name="selectPages"
                popoverProps={{ portalContainer: popoverBox ?? undefined }}
                selectedKeys={pageExport}
                variant="bordered"
                onSelectionChange={onChangePageExport}
              >
                {(item) => (
                  <SelectItem key={item.id}>{item.label}</SelectItem>
                )}
              </Select>
            </div>
          )}

          {isVideoExport && (
            <>
              <Input
                aria-label='videoDuration'
                label="Duration (seconds)"
                max={60}
                min={1}
                type="number"
                value={String(videoDuration)}
                variant="bordered"
                onValueChange={(value) => setVideoDuration(Number(value) || 1)}
              />

              <Checkbox
                color='default'
                css={{ letterSpacing: .3, gap: 4 }}
                isSelected={isComposite}
                onValueChange={setIsComposite}
              >
                Add brand intro
              </Checkbox>

              {isComposite && (
                <>
                  <Select
                    aria-label='brand'
                    className="max-w-xs text-black"
                    color="primary"
                    items={brands.map((item) => ({ label: item, value: item }))}
                    name="brand"
                    placeholder="Select a brand"
                    popoverProps={{ portalContainer: popoverBox ?? undefined }}
                    selectedKeys={brand ? new Set([brand]) : new Set([])}
                    variant="bordered"
                    onSelectionChange={(value) => setBrand(Array.from(value)[0] as string)}
                  >
                    {(item) => <SelectItem key={item.value}>{item.label}</SelectItem>}
                  </Select>

                  <Input
                    aria-label='dateTime'
                    label="Date/time overlay"
                    type="datetime-local"
                    value={dateTime}
                    variant="bordered"
                    onValueChange={setDateTime}
                  />
                </>
              )}
            </>
          )}

          <Button
            css={{
              background: '#3a3a4c',
              color: '#fff',
              padding: '8px 16px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 40,
              lineHeight: 40,
            }}
            disabled={isLoading}
            isLoading={isLoading}
            spinner={<Spinner className='mr-2' color="default" size="sm" />}
            onClick={() => onDownload()}
          >
            {isLoading ? (isVideoExport ? 'Generating video' : 'Downloading') : 'Download'}
          </Button>
        </div>
      </Popover>
    </>
  );
};
